import React, { forwardRef, useImperativeHandle, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import { setValueBrands } from '../../redux/productSlice';
import CustomInput from '../../components/customInput/CustomInput';
import ModalFileUpload from '../../components/ModalFileUpload/ModalFileUpload';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';
import { uploadAsset, deleteAsset } from '../../redux/assetSlice';
import { ASSET_LOCATION_MAPPING, ASSET_TYPE_MAPPING, LABEL_DELETE_ASSET } from '../../utils/constant';
import { isEmpty } from '../../utils/commonUtil';

const Brands = forwardRef(({ language, productId, formType, onShowAlert }, ref) => {
  const dispatch = useDispatch();
  const productState = useSelector((state) => state.product);
  const product = productState.data || {};

  const brandsData = product.brands || product.contentData?.brands || {};
  const brandsItems = brandsData.src || [];
  const brandsTitle = brandsData.title || '';
  const brandsDescription = brandsData.description || '';

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [isShowDeleteAsset, setIsShowDeleteAsset] = useState(null);
  const [selectedBrandIndex, setSelectedBrandIndex] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [showDeleteBrandModal, setShowDeleteBrandModal] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);

  const PLACEHOLDER_CONFIG = {
    title: {
      EN: "Enter brands title EN",
      CN: "Enter brands title CN"
    },
    description: {
      EN: "Enter brands description EN",
      CN: "Enter brands description CN"
    },
    iconUrl: {
      EN: "upload icon",
      CN: "upload icon"
    },
    brandDescription: {
      EN: "Enter brand description EN",
      CN: "Enter brand description CN"
    },
    retainBrand: {
      EN: "At least retain one brand item EN",
      CN: "At least retain one brand item CN"
    }
  };

  const INITIAL_BRAND_ITEM = {
    iconUrl: '',
    iconFileId: '',
    description: '',
    width: 64,
    height: 64
  };

  useEffect(() => {
    if (!brandsData.title && brandsItems.length === 0) {
      dispatch(setValueBrands({
        value: {
          title: '',
          description: '',
          src: [INITIAL_BRAND_ITEM]
        }
      }));
    }
  }, [brandsData, brandsItems.length, dispatch]);

  const handleFileUpload = (file) => {
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("uuid", formType === 'EDIT' ? productId : "");
    formData.append("assetLocation", ASSET_LOCATION_MAPPING[selectedField].brands);
    formData.append("assetType", ASSET_TYPE_MAPPING.product);
    formData.append("file", file);

    dispatch(uploadAsset(formData))
      .then((res) => {
        if (res?.payload) {
          const fileId = res.payload.resdata.fileId;
          const fileUrl = res.payload.resdata.url;

          dispatch((dispatch, getState) => {
            const state = getState();
            const product = state.product.data || {};
            const brandsData = product.brands || product.contentData?.brands || {};
            const currentBrandItems = brandsData.src || [];

            const newItems = currentBrandItems.map((item, i) => {
              if (i === selectedBrandIndex) {
                return {
                  ...item,
                  iconUrl: fileUrl,
                  iconFileId: fileId
                };
              }
              return item;
            });

            dispatch(setValueBrands({
              value: {
                ...brandsData,
                src: newItems
              }
            }));
          });
        }
        if (res?.error) {
          onShowAlert(res.error.message, false);
        }
      })
      .catch((err) => {
        onShowAlert(err.message || "Upload failed", false);
      })
      .finally(() => {
        setIsUploading(false);
        setShowFileUploadModal(false);
        setSelectedBrandIndex(null);
        setSelectedField(null);
      });
  };

  const handleDeleteFile = () => {
    const fileId = brandsItems[selectedBrandIndex]?.iconFileId;

    const newItems = brandsItems.map((item, i) => {
      if (i === selectedBrandIndex) {
        return {
          ...item,
          iconUrl: "",
          iconFileId: ""
        };
      }
      return item;
    });

    dispatch(setValueBrands({
      value: {
        ...brandsData,
        src: newItems
      }
    }));

    if (fileId) {
      dispatch(deleteAsset(fileId))
        .then((res) => {
          if (res?.error &&
            !res.error.message.includes("No active document") &&
            !res.error.message.includes("Unexpected token")) {
            onShowAlert(res.error.message, false);
          }
        })
        .catch((err) => {
          if (err.message &&
            !err.message.includes("No active document") &&
            !err.message.includes("Unexpected token")) {
            onShowAlert(err.message, false);
          }
        })
        .finally(() => {
          setIsShowDeleteAsset(null);
          setSelectedBrandIndex(null);
          setSelectedField(null);
        });
    } else {
      setIsShowDeleteAsset(null);
      setSelectedBrandIndex(null);
      setSelectedField(null);
    }
  };

  const handleShowUploadFile = (brandIndex, field) => {
    setSelectedBrandIndex(brandIndex);
    setSelectedField(field);
    setShowFileUploadModal(true);
  };

  const handleShowDeleteFile = (brandIndex, field) => {
    setSelectedBrandIndex(brandIndex);
    setSelectedField(field);
    setIsShowDeleteAsset(LABEL_DELETE_ASSET);
  };

  const handleCancelUploadFile = () => {
    setShowFileUploadModal(false);
    setSelectedBrandIndex(null);
    setSelectedField(null);
  };

  const addGroup = () => {
    dispatch(setValueBrands({
      value: {
        ...brandsData,
        src: [...brandsItems, INITIAL_BRAND_ITEM]
      }
    }));
  };

  const deleteGroup = (index) => {
    if (brandsItems.length <= 1) {
      alert(PLACEHOLDER_CONFIG.retainBrand[language]);
      return;
    }
    setBrandToDelete(index);
    setShowDeleteBrandModal(true);
  };

  const handleConfirmDeleteBrand = () => {
    if (brandToDelete !== null) {
      const newItems = brandsItems.filter((_, i) => i !== brandToDelete);
      dispatch(setValueBrands({
        value: {
          ...brandsData,
          src: newItems
        }
      }));
    }
    setShowDeleteBrandModal(false);
    setBrandToDelete(null);
  };

  const updateBrands = (field, value) => {
    dispatch(setValueBrands({
      value: { ...brandsData, [field]: value }
    }));
  };

  const updateBrandItem = (index, field, value) => {
    const newItems = brandsItems.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    dispatch(setValueBrands({
      value: {
        ...brandsData,
        src: newItems
      }
    }));
  };

  const validateAllBrands = () => ({});

  useImperativeHandle(ref, () => ({
    addGroup,
    validateAllBrands
  }));

  const renderFileUploadSection = (item, index) => (
    <div className='package-input'>
      <label>Icon:</label>
      <input
        type="text"
        placeholder={PLACEHOLDER_CONFIG.iconUrl[language]}
        value={item.iconUrl || ''}
        disabled
        style={{ width: '73%' }}
      />
      {!item.iconUrl ? (
        <button
          className="upload-btn"
          onClick={() => handleShowUploadFile(index, "iconUrl")}
        >
          Upload
        </button>
      ) : (
        <button
          className="icon-btn"
          onClick={() => handleShowDeleteFile(index, "iconUrl")}
        >
          <img src={DeleteIcon} alt="Delete" className="action-icon" />
        </button>
      )}
    </div>
  );

  const renderBrandItem = (item, index) => (
    <div key={index}>
      <div className="domain-group">
        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>Brand Description:</label>
          </div>

          <CustomInput
            type="text"
            value={item.description || ''}
            onChange={(e) => updateBrandItem(index, 'description', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.brandDescription[language]}
            customStyle={{ width: '73%' }}
          />
          <button
            className="icon-btn"
            onClick={() => deleteGroup(index)}
            style={{ marginLeft: '12px' }}
          >
            <img src={DeleteIcon} alt="Delete" className="campaign-delete-icon" />
          </button>

        </div>

        {renderFileUploadSection(item, index)}

        {item.iconUrl && (
          <div style={{ marginLeft: '190px', marginBottom: '20px' }}>
            <img
              src={`https://uat.stack-ez.com${item.iconUrl}`}
              alt="Preview"
              style={{ maxWidth: '200px', maxHeight: '200px', marginTop: '10px' }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '20px', width: '35%' }}>
          <div className='package-input' >
            <label>Width:</label>
            <input
              type="number"
              value={item.width === '' ? '' : (item.width || 64)}
              onChange={(e) => updateBrandItem(index, 'width', e.target.value === '' ? '' : parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '20px', width: '35%' }}>
          <div className='package-input'>
            <label>Height:</label>
            <input
              type="number"
              value={item.height === '' ? '' : (item.height || 64)}
              onChange={(e) => updateBrandItem(index, 'height', e.target.value === '' ? '' : parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>

      {
        index !== brandsItems.length - 1 && (
          <div
            style={{
              height: '0.5px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              margin: '25px -40px',
              width: 'calc(100% + 80px)',
              display: 'block',
              boxSizing: 'border-box'
            }}
          />
        )
      }
    </div >
  );

  return (
    <div className="tab-package-container">
      <div className="domain-group">
        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>Title:</label>
          </div>
          <CustomInput
            type="text"
            value={brandsTitle}
            onChange={(e) => updateBrands('title', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.title[language]}
            customStyle={{ width: '73%' }}
          />
        </div>

        <div className='package-input'>
          <label>Description:</label>
          <textarea
            placeholder={PLACEHOLDER_CONFIG.description[language]}
            value={brandsDescription || ''}
            onChange={(e) => updateBrands('description', e.target.value)}
            rows={3}
            style={{ width: '73%' }}
          />
        </div>
      </div>

      <div
        style={{
          height: '0.5px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          margin: '25px -40px',
          width: 'calc(100% + 80px)',
          display: 'block',
          boxSizing: 'border-box'
        }}
      />

      {brandsItems.map(renderBrandItem)}

      <ModalFileUpload
        show={showFileUploadModal}
        handleCancel={handleCancelUploadFile}
        handleUpload={handleFileUpload}
        isLoading={isUploading}
      />

      <CommonDeleteModal
        show={!isEmpty(isShowDeleteAsset)}
        onHide={() => setIsShowDeleteAsset(null)}
        onConfirm={handleDeleteFile}
      />

      <CommonDeleteModal
        show={showDeleteBrandModal}
        onHide={() => {
          setShowDeleteBrandModal(false);
          setBrandToDelete(null);
        }}
        onConfirm={handleConfirmDeleteBrand}
        title="Delete Brand"
        instruction="Are you sure you want to delete this brand item?"
      />
    </div>
  );
});

export default Brands;